import MessageReactions from './MessageReactions';
import React, { useMemo, useCallback } from 'react';
import { StyleSheet, Platform, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import moment from 'moment-mini';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import MessageActions from '@/components/Messages/MessageActions';
import MessageBotAvatar from '@/components/Messages/MessageBotAvatar';
import MessageContent from '@/components/Messages/MessageContent';
import MessageButtons from '@/components/Messages/MessageButtons';
import MessageFiles from '@/components/Messages/MessageFiles';
import MessageQuote from '@/components/Messages/MessageQuote';
import useResponsiveness from '@/hooks/useResponsiveness';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import MessageThinkingContent from '@/components/Messages/MessageThinkingContent';
import MessageExpandable from '@/components/Messages/MessageExpandable';
import MessageImages from './MessageImages';
import { imagesNotCoveredByFiles } from '@/constants/utils/message_file_groups';
import CreditsDepletedMessage from '@/components/Messages/CreditsDepletedMessage';
import PlusRequiredMessage from '@/components/Messages/PlusRequiredMessage';
import ContentModerationMessage from '@/components/Messages/ContentModerationMessage';
import { useAuthStore, useChatStore, useModalStore, useModelStore, useUIStore } from '@/store';
import Icon from '@/components/Icon';
import { AGENT_ICON, MESSAGE_LAYOUT, MESSAGE_WIDTH, MESSAGE_WIDTH_AMOUNTS, RESPONSE_ACTION_TYPES } from '@/constants/config';
import { isDarkTheme } from '@/constants/Colors';
import { Image } from 'expo-image';
import { useRouter } from 'expo-router';
import { openUploadedChatFile, openMessageFile } from '@/constants/utils/files';
import {
  messageHasEditorInsertContent,
  messageHasMarkdownTable,
} from '@/constants/utils/message_editor_insert';
import MessageInferenceJob from '@/components/Messages/MessageInferenceJob';
import { getMessageDisplayInfo, computeImageAwareIsWaiting, shouldShowMessageTimestamp } from '@/constants/utils/message';
import { isCreditMessageError as isCreditError, isPlusMessageError as isPlusError } from '@/constants/utils/response_errors';
import { getTemporaryUserBubbleStyle } from '@/constants/utils/temporaryChatStyles';

// Helper to check if error is a content moderation rejection
const isModerationError = (message) => {
  return message?.buttons?.some(btn => btn?.action === RESPONSE_ACTION_TYPES.CONTENT_MODERATION);
};

const createStyles = (colors, isXS, isUser, isLastMessage, messageWidth, showMessageActions) => StyleSheet.create({
  container: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    paddingTop: 4,
    paddingBottom: 8,
    paddingHorizontal: 0,
    position: 'relative',
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '100%',
    maxWidth: 
      messageWidth === MESSAGE_WIDTH.NARROW ? MESSAGE_WIDTH_AMOUNTS.NARROW : 
      messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM : '100%',
    ...(Platform.OS === 'web' && {
      cursor: 'default',
      ':hover': {
        backgroundColor: colors.solid_color_secondary,
      },
    }),
  },
  messageWrapper: {
    ...(Platform.OS === 'web'
      ? {
          flex: 1,
          minWidth: 0,
          height: 'auto',
          // Opacity only — height/padding/transform transitions cause scroll
          // jumps on mobile browsers when streaming ends and layout settles.
          transition: 'opacity 0.3s ease-out',
          maxWidth: '100%',
          overflow: 'hidden',
        }
      : { flexGrow: 1, flexShrink: 1, minWidth: 0, maxWidth: '100%', overflow: 'hidden' }),
    position: 'relative',
    paddingBottom: (!isUser && isLastMessage) ? 8 : 0,
  },
  messageActionsContainer: {
    position: showMessageActions ? 'relative' : 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    minWidth: 120,
    height: ((!isUser && isLastMessage) || showMessageActions) ? 36 : 0,
    overflow: 'hidden',
    opacity: ((!isUser && isLastMessage) || showMessageActions) ? 1 : 0,
    ...(Platform.OS === 'web' ? {
      transition: 'opacity 0.3s ease-out',
      transform: ((!isUser && isLastMessage) || showMessageActions) ? 'translateY(5px)' : 'translateY(-15px)',
      pointerEvents: isUser ? 'none' : 'auto',
    } : {
      transform: [{ translateY: ((!isUser && isLastMessage) || showMessageActions) ? 5 : -15 }],
    }),
  },
  avatar: {
    width: isXS ? 20 : 30,
    height: isXS ? 20 : 30,
    borderRadius: 99,
    marginRight: isXS ? 8 : 12,
    backgroundColor: isUser ? 'transparent' : colors.solid_color_tertiary,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'visible',
    position: 'relative',
  },
  avatarImage: {
    width: isXS ? 20 : 30,
    height: isXS ? 20 : 30,
    borderRadius: 99,
  },
  stackedAvatar: {
    position: 'absolute',
    borderWidth: 2,
    borderColor: colors.solid_color_primary,
  },
  content: {
    flex: 1,
    minWidth: 0,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 3,
  },
  username: {
    marginRight: 3,
    flexShrink: 1,
  },
  timestamp: {
    marginLeft: 3,
  },
  modelAgentNames: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  message: {
    fontSize: 14,
    color: colors.text_color_primary,
    lineHeight: 20,
  },
  errorContainer: {
    backgroundColor: 'rgba(254, 0, 0, 0.15)',
    borderColor: 'rgba(254, 0, 0, 0.25)',
    borderLeftWidth: 2,
    paddingLeft: 12,
    marginLeft: 2,
    paddingVertical: 6,
    marginTop: 4,
    maxWidth: '100%',
  },
  errorContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  errorContentWrapper: {
    marginTop: 5,
  },
  errorIcon: {
    color: '#DC2626',
    marginRight: 8,
    marginTop: 0,
    flexShrink: 0,
  },
  errorText: {
    fontSize: 14,
    lineHeight: 20,
    color: '#991B1B',
    flex: 1,
  },
  messageContentWrapper: {
    flex: 1,
    minWidth: 0,
    maxWidth: '100%',
    ...(Platform.OS === 'web' && { overflow: 'hidden' }),
  },
  preferenceButtonContainer: {
    marginTop: 6,
    marginBottom: 4,
    alignItems: 'flex-start',
  },
});

const formatMessageTime = (timestamp) => {
  const messageTime = moment(timestamp || Date.now());
  const now = moment();
  const diffDays = now.diff(messageTime, 'days');
  
  const time = messageTime.format('LT');
  
  if (diffDays === 0) {
    return time;
  } else if (diffDays === 1) {
    return `${time} · Yesterday`;
  } else if (diffDays <= 3) {
    return `${time} · ${messageTime.format('ddd')}`;
  } else {
    return `${time} · ${messageTime.format('D MMM')}`;
  }
};

// Model dropdown component
const ModelDropdown = React.memo(({ model, children }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const { openModal } = useModalStore(state => state.actions);
  const router = useRouter();

  const handleOnSelectModel = () => {
    openModal('ModelDetailsModal', { modelId: model.id });
  };

  const handleOnStartNewChatWithModel = () => {
    router.push(`/m/${model.id}`);
  };

  const handleOnViewAllModels = () => {
    if (model?.company?.id) {
      router.push(`/models?company=${model.company.id}`);
    } else {
      router.push('/models');
    }
  };

  const attribution = model?.company?.attribution?.replace('{model}', model.name);

  return (
    <DropdownMenu.Root onOpenChange={setIsOpen}>
      <DropdownMenu.Trigger>
        {children}
      </DropdownMenu.Trigger>
      <DropdownMenu.Content 
        className={Platform.OS === 'web' ? 'DropdownMenuContent' : undefined}
        sideOffset={5}
        align="start"
        style={{ maxWidth: 200 }}
      >
        {Platform.OS === 'web' ? (
          <>
            {!!attribution && (
              <View style={{
                padding: 10,
                borderRadius: 8,
                backgroundColor: 'rgba(190, 190, 190, 0.2)',
                marginBottom: 8,
              }}>
                <Text size="s"  color="secondary" style={{ fontStyle: 'italic', opacity: 1 }}>
                  {attribution}
                </Text>
              </View>
            )}

            <DropdownMenuItem
              key="model-details"
              title='View model details'
              icon="circle-info"
              onSelect={handleOnSelectModel}
            />
            {model.company && (
              <DropdownMenuItem
                key="model-company"
                title={`View all models by ${model?.company?.name}`}
                icon="layer-group"
                onSelect={handleOnViewAllModels}
              />
            )}
            <DropdownMenuItem
              key="model-new-chat"
              title='Start a new chat with this model'
              icon="pen-to-square"
              onSelect={handleOnStartNewChatWithModel}
            />
          </>
        ) : (
          // Native menus drop anything that isn't a DropdownMenu.Item, so these
          // are written out raw. The attribution note has no native equivalent.
          <>
            <DropdownMenu.Item key="model-details" onSelect={handleOnSelectModel}>
              <DropdownMenu.ItemIcon ios={{ name: 'info.circle' }} />
              <DropdownMenu.ItemTitle>View model details</DropdownMenu.ItemTitle>
            </DropdownMenu.Item>
            {model.company && (
              <DropdownMenu.Item key="model-company" onSelect={handleOnViewAllModels}>
                <DropdownMenu.ItemIcon ios={{ name: 'square.grid.2x2' }} />
                <DropdownMenu.ItemTitle>
                  {`View all models by ${model?.company?.name}`}
                </DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
            )}
            <DropdownMenu.Item key="model-new-chat" onSelect={handleOnStartNewChatWithModel}>
              <DropdownMenu.ItemIcon ios={{ name: 'square.and.pencil' }} />
              <DropdownMenu.ItemTitle>Start a new chat with this model</DropdownMenu.ItemTitle>
            </DropdownMenu.Item>
          </>
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
});

// Agent dropdown component
const AgentDropdown = React.memo(({ agent, children }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const { openModal } = useModalStore(state => state.actions);
  const router = useRouter();

  const handleOnSelectAgent = () => {
    openModal('AgentDetailsModal', { agentId: agent.id });
  };

  const handleOnStartNewChatWithAgent = () => {
    router.push(`/g/${agent.id}`);
  };

  const handleOnViewAllAgents = () => {
    router.push('/agents');
  };

  return (
    <DropdownMenu.Root onOpenChange={setIsOpen}>
      <DropdownMenu.Trigger>
        {children}
      </DropdownMenu.Trigger>
      <DropdownMenu.Content 
        className={Platform.OS === 'web' ? 'DropdownMenuContent' : undefined}
        sideOffset={5}
        align="start"
      >
        {Platform.OS === 'web' ? (
          <>
            <DropdownMenuItem
              key="agent-details"
              title={agent.name}
              description={agent.description}
              imageUrl={agent.avatarPath}
              onSelect={handleOnSelectAgent}
            />
            <DropdownMenuItem
              key="agent-view-all"
              title="View all agents"
              icon="train"
              onSelect={handleOnViewAllAgents}
            />
            <DropdownMenuItem
              key="agent-new-chat"
              title="Start a new chat"
              icon="pen-to-square"
              onSelect={handleOnStartNewChatWithAgent}
            />
          </>
        ) : (
          <>
            <DropdownMenu.Item key="agent-details" onSelect={handleOnSelectAgent}>
              <DropdownMenu.ItemIcon ios={{ name: 'info.circle' }} />
              <DropdownMenu.ItemTitle>{agent.name}</DropdownMenu.ItemTitle>
            </DropdownMenu.Item>
            <DropdownMenu.Item key="agent-view-all" onSelect={handleOnViewAllAgents}>
              <DropdownMenu.ItemIcon ios={{ name: 'square.grid.2x2' }} />
              <DropdownMenu.ItemTitle>View all agents</DropdownMenu.ItemTitle>
            </DropdownMenu.Item>
            <DropdownMenu.Item key="agent-new-chat" onSelect={handleOnStartNewChatWithAgent}>
              <DropdownMenu.ItemIcon ios={{ name: 'square.and.pencil' }} />
              <DropdownMenu.ItemTitle>Start a new chat</DropdownMenu.ItemTitle>
            </DropdownMenu.Item>
          </>
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
});

// Memoized avatar component
const Avatar = React.memo(({ isUser, userAvatar, agents, models, isXS, colors, styles, openAgentDetailsModal, openModelDetailsModal }) => {
  if (isUser) {
    return (
      <View style={styles.avatar}>
        {userAvatar ? (
          <Image
            source={{ uri: userAvatar }}
            style={styles.avatarImage}
            contentFit="cover"
          />
        ) : (
          <Icon 
            icon="circle-user"
          size={isXS ? 20 : 30}
            color={colors.solid_color_tertiary}
          />
        )}
      </View>
    );
  }

  const primaryAgent = agents?.[0];
  if (primaryAgent) {
    return <AgentDropdown agent={primaryAgent}><MessageBotAvatar agent={primaryAgent} /></AgentDropdown>;
  }

  const primaryModel = models?.[0];
  const primaryEntity = primaryModel || primaryAgent;

  // If no entity exists, show default assistant icon
  if (!primaryEntity) {
    return (
      <View style={styles.avatar}>
        <Icon 
          icon={AGENT_ICON}
          size={isXS ? 12 : 20}
          color={colors.text_color_tertiary}
        />
      </View>
    );
  }

  const avatarContent = (
    <View style={styles.avatar}>
      <Image
        source={{ uri: primaryEntity.avatarPath || primaryEntity.logoPath }}
        style={styles.avatarImage}
        contentFit="cover"
      />
    </View>
  );

  // Wrap with appropriate dropdown
  if (primaryModel) {
    return (
      <ModelDropdown model={primaryModel}>
        {avatarContent}
      </ModelDropdown>
    );
  } else if (primaryAgent) {
    return (
      <AgentDropdown agent={primaryAgent}>
        {avatarContent}
      </AgentDropdown>
    );
  }

  return avatarContent;
});

// Memoized header component
const MessageHeader = React.memo(({
  isUser,
  userName,
  message,
  agents,
  models,
  styles,
  showTimestamps,
  openAgentDetailsModal,
  openModelDetailsModal
}) => {
  if (isUser) {
    return (
      <View style={styles.header}>
        <Text color='primary' size='n' weight='medium' style={styles.username}>
          {userName}
        </Text>
        {shouldShowMessageTimestamp(message, { isUser: true, showTimestamps }) && (
          <Text color='tertiary' size='s' style={styles.timestamp}>
            {formatMessageTime(message.timestamp)}
          </Text>
        )}
      </View>
    );
  }

  const modelNames = models?.map(m => m.name).join(', ') || 'Assistant';
  const agentNames = agents?.map(a => a.name).join(', ');
  
  return (
    <View style={styles.header}>
      <View style={styles.modelAgentNames}>
        {models?.[0] ? (
          <ModelDropdown model={models[0]}>
            <Text color='primary' size='n' weight='medium' style={[styles.username, Platform.OS === 'web' && { cursor: 'pointer' }]}>
              {modelNames}
            </Text>
          </ModelDropdown>
        ) : (
          <Text color='primary' size='n' weight='medium' style={styles.username}>
            {modelNames}
          </Text>
        )}
        {agents?.length > 0 && (
          <>
            <Text color='tertiary' size='n' style={styles.username}>using</Text>
            <AgentDropdown agent={agents[0]}>
              <Text color='primary' size='n' style={[styles.username, Platform.OS === 'web' && { cursor: 'pointer' }]}>
                {agentNames}
              </Text>
            </AgentDropdown>
          </>
        )}
      </View>
      {shouldShowMessageTimestamp(message, { isUser: false, showTimestamps }) && (
        <Text color='tertiary' size='s' style={styles.timestamp}>
          {formatMessageTime(message.timestamp)}
        </Text>
      )}
    </View>
  );
});

// Memoized error message component
const ErrorMessage = React.memo(({ conversationId, message, styles, colors, isUser }) => (
  <View style={styles.errorContainer}>
    <View style={styles.errorContent}>
      <View style={styles.errorContentWrapper}>
        <Icon 
          name="circle-exclamation" 
          size={16} 
          color={colors.color_destructive}
          style={styles.errorIcon}
        />
      </View>
      <View style={styles.messageContentWrapper}>
        <MessageContent 
          conversationId={conversationId}
          messageId={message.id}
          content={message.content}
          citations={message.citations}
          products={message.products}
          stockQuotes={message.stockQuotes}
          places={message.places}
          map={message.map}
          messageLayout={MESSAGE_LAYOUT.WIDE}
          isUser={isUser}
          isError={message.isError}
          hasError={message.hasError}
          isRequestingMedia={message.isRequestingMedia}
          textStyle={styles.errorText}
          modelIds={message.modelIds}
          agentIds={message.agentIds}
          isDeleted={message.flags?.isDeleted}
        />
      </View>
    </View>
  </View>
));

// Memoized regular message component
const RegularMessage = React.memo(({ conversationId, message, isUser }) => (
  <MessageContent
    conversationId={conversationId}
    messageId={message.id}
    content={message.content} 
    citations={message.citations}
    products={message.products}
    stockQuotes={message.stockQuotes}
    places={message.places}
    map={message.map}
    messageLayout={MESSAGE_LAYOUT.SLIM}
    isUser={isUser}
    isStreaming={message.isStreaming}
    isWaiting={computeImageAwareIsWaiting(message)}
    isTyping={message.isTyping}
    isRequestingMedia={message.isRequestingMedia}
    modelIds={message.modelIds}
    agentIds={message.agentIds}
    isDeleted={message.flags?.isDeleted}
    inferenceStatus={message.inferenceStatus}
    inferenceStage={message.inferenceStage}
    inferenceElapsedMs={message.inferenceElapsedMs}
  />
));

// Memoized message content component
const MessageContentWrapper = React.memo(({ 
  conversationId,
  message, 
  isUser,
  agents,
  models,
  styles, 
  colors,
  userName,
  showTimestamps,
  onImageClick,
  onButtonClick,
  openAgentDetailsModal,
  openModelDetailsModal,
  onOpenLightbox,
  onOpenFileEditor,
}) => {
  const { theme } = useTheme();
  const isTemporaryChat = useChatStore((state) => (
    state.chats.find(c => c.id === conversationId)?.flags?.isTemporary === true
  ));
  const temporaryStyle = useMemo(
    () => (isUser && isTemporaryChat ? getTemporaryUserBubbleStyle(theme) : null),
    [isUser, isTemporaryChat, theme],
  );
  const { hasAnyInferencesWithIncompleteJobs, badStatuses } = getMessageDisplayInfo(message, isUser);

  return (
    <View style={[styles.content, isUser && temporaryStyle, isUser && temporaryStyle && { paddingHorizontal: 10, paddingVertical: 8 }]}>
      <MessageHeader 
        isUser={isUser} 
        message={message} 
        styles={styles}
        agents={agents}
        models={models}
        userName={userName}
        showTimestamps={showTimestamps}
        openAgentDetailsModal={openAgentDetailsModal}
        openModelDetailsModal={openModelDetailsModal}
      />
      {!!message.thinkingContent && !message.isThinking && !message.isStreaming && !message.isWaiting && !message.isRequestingMedia && (
        <MessageThinkingContent
          conversationId={conversationId}
          messageId={message.id}
          thinkingContent={message.thinkingContent}
          thinkingDurationMs={message.thinkingDurationMs}
          thinkingStartTime={message.thinkingStartTime}
          isThinking={message.isThinking}
          inferenceStatus={message.inferenceStatus}
          inferenceStage={message.inferenceStage}
          messageLayout={MESSAGE_LAYOUT.SLIM}
        />
      )}
      {isUser && !!message.quoteContent && (
        <MessageQuote quoteContent={message.quoteContent} />
      )}
      {/* Check if this is a credit-related error (show friendly message instead of error UI) */}
      {(message.isError || message.hasError) && isCreditError(message) ? (
        <CreditsDepletedMessage message={message} />
      ) : (message.isError || message.hasError) && isPlusError(message) ? (
        <PlusRequiredMessage message={message} />
      ) : (message.isError || message.hasError) && isModerationError(message) ? (
        <ContentModerationMessage message={message} />
      ) : message.isError || message.hasError || message?.flags?.isDeleted ? (
        <ErrorMessage message={message} styles={styles} colors={colors} isUser={isUser} />
      ) : (
        <>
          {isUser ? (
            <MessageExpandable
              content={message.content}
              fadeColor={colors.solid_color_primary}
              align="left"
            >
              <RegularMessage conversationId={conversationId} message={message} isUser={isUser} />
            </MessageExpandable>
          ) : (
            <RegularMessage conversationId={conversationId} message={message} isUser={isUser} />
          )}
          {isUser && message.isEdited && (
            <Text size="xs" color="secondary" style={{ opacity: 0.6, marginTop: 2 }}>
              (edited)
            </Text>
          )}
          {hasAnyInferencesWithIncompleteJobs && (
            <View style={{ marginTop: 8 }}>
              {message.inferences.map((inf) => (inf?.output?.jobs || [])
                .filter((job) => badStatuses.has(job?.status))
                .map((job) => (
                  <MessageInferenceJob
                    key={`${inf.id || 'inf'}-${job.id || Math.random()}`}
                    conversationId={conversationId}
                    messageId={message.id}
                    inferenceId={inf.id}
                    job={job}
                  />
                ))
              )}
            </View>
          )}
        </>
      )}
      {message.images && (
        <MessageImages
          conversationId={conversationId}
          images={imagesNotCoveredByFiles(message.images, message.files)}
          onImageClick={onImageClick}
        />
      )}
      {Array.isArray(message.files) && message.files.length > 0 && (
        <MessageFiles
          files={message.files}
          isSmall={isUser}
          align='left'
          isEditable={!isUser}
          onEdit={(file) => {
            if (file && file?.cdnPath && !isUser) {
              openMessageFile(file, {
                conversationId,
                onOpenLightbox,
                openToEditor: true,
                withInpainting: true,
                withChatInput: true,
              });
            } else if (file && file?.id && isUser) {
              // Uploaded docx/xlsx/csv/pptx open in their editor on desktop
              // web; everything else keeps the file details modal.
              if (!openUploadedChatFile(file, {
                conversationId,
                onOpenLightbox,
                openToEditor: false,
                withInpainting: true,
                withChatInput: true,
              })) onOpenFileEditor(file.id);
            } else {
              console.log('File EDIT pressed but do nothing:', file);
            }
          }}
          onPress={(file) => {
            if (file && file?.cdnPath && !isUser) {
              openMessageFile(file, {
                conversationId,
                onOpenLightbox,
                openToEditor: false,
                withInpainting: true,
                withChatInput: true,
              });
            } else if (file && file?.id && isUser) {
              // Uploaded docx/xlsx/csv/pptx open in their editor on desktop
              // web; everything else keeps the file details modal.
              if (!openUploadedChatFile(file, {
                conversationId,
                onOpenLightbox,
                openToEditor: false,
                withInpainting: true,
                withChatInput: true,
              })) onOpenFileEditor(file.id);
            } else {
              console.log('File pressed but do nothing:', file);
            }
          }}
        />
      )}
      {isUser && <MessageReactions conversationId={conversationId} message={message} />}
      {/* Don't show MessageButtons for credit/Plus/moderation errors since their message components handle the CTA */}
      {!((message.isError || message.hasError) && (isCreditError(message) || isPlusError(message) || isModerationError(message))) && (
        <MessageButtons conversationId={conversationId} messageId={message.id} buttons={message.buttons} onButtonClick={onButtonClick} />
      )}
    </View>
  );
}, (prevProps, nextProps) => {
  if (prevProps.showTimestamps !== nextProps.showTimestamps
    || prevProps.agents !== nextProps.agents
    || prevProps.models !== nextProps.models
    || prevProps.isUser !== nextProps.isUser) return false;
  const prev = prevProps.message;
  const next = nextProps.message;
  return (
    prev.conversationId === next.conversationId &&
    prev.isError === next.isError &&
    prev.hasError === next.hasError &&
    prev.errorType === next.errorType &&
    prev.reactions?.agent === next.reactions?.agent &&
    prev.content === next.content &&
    prev.thinkingContent === next.thinkingContent &&
    prev.thinkingDurationMs === next.thinkingDurationMs &&
    prev.thinkingStartTime === next.thinkingStartTime &&
    prev.inferenceStatus === next.inferenceStatus &&
    prev.inferenceStage === next.inferenceStage &&
    prev.inferenceElapsedMs === next.inferenceElapsedMs &&
    prev.isThinking === next.isThinking &&
    prev.isStreaming === next.isStreaming &&
    prev.isWaiting === next.isWaiting &&
    prev.isTyping === next.isTyping &&
    prev.isEdited === next.isEdited &&
    prev.flags?.isDeleted === next.flags?.isDeleted &&
    prev.flags?.isGroupedResponsePreferred === next.flags?.isGroupedResponsePreferred &&
    // Arrays are replaced wholesale by store updates (never mutated in
    // place) — reference equality is sufficient (see Message.jsx).
    prev.images === next.images &&
    prev.buttons === next.buttons &&
    prev.citations === next.citations &&
    prev.products === next.products &&
    prev.stockQuotes === next.stockQuotes &&
    prev.places === next.places &&
    prev.map === next.map &&
    prev.modelIds === next.modelIds &&
    prev.agentIds === next.agentIds &&
    prev.inferences === next.inferences &&
    prev.files === next.files
  );
});

// Memoized mobile message component
const MobileMessage = React.memo(({ 
  conversationId,
  message,
  agents,
  models,
  userName,
  userAvatar,
  isUser,
  isLastMessage,
  styles,
  colors,
  theme,
  isXS,
  showActions,
  showCopy,
  showAddToEditor,
  showAddToDocument,
  showAddToSpreadsheet,
  showTimestamps,
  onCopy,
  onAddToEditor,
  onAddToDocument,
  onAddToSpreadsheet,
  onShare,
  onSpeak,
  onRegenerate,
  onFork,
  onStartNewTask,
  onBookmark,
  onFeedback,
  onEdit,
  onReport,
  onDelete,
  onExport,
  onImageClick,
  onButtonClick,
  openAgentDetailsModal,
  openModelDetailsModal,
  onOpenLightbox,
  onOpenFileEditor,
  showPreferenceButton,
  showPreferenceIcons,
  onPreferResponse,
  onRejectResponse,
  isInMessageGroup,
  isAutoMode = false,
  displayModelIds,
}) => (
  <View style={styles.container}>
    <Avatar
      isUser={isUser}
      isXS={isXS}
      colors={colors}
      styles={styles}
      agents={agents}
      models={models}
      openAgentDetailsModal={openAgentDetailsModal}
      openModelDetailsModal={openModelDetailsModal}
    />
    <View style={styles.content}>
      <MessageContentWrapper
        conversationId={conversationId}
        messageId={message.id}
        message={message}
        agents={agents}
        models={models}
        isUser={isUser}
        styles={styles}
        colors={colors}
        showTimestamps={showTimestamps}
        onImageClick={onImageClick}
        onButtonClick={onButtonClick}
        openAgentDetailsModal={openAgentDetailsModal}
        openModelDetailsModal={openModelDetailsModal}
        onOpenLightbox={onOpenLightbox}
        onOpenFileEditor={onOpenFileEditor}
      />
    </View>
    {showActions && (
      <View style={{ marginTop: 4 }}>
        <MessageActions
              conversationId={conversationId}
              message={message}
          isUser={isUser}
          userName={userName}
          userAvatar={userAvatar}
          isVisible={true}
          isCompact={true}
          onCopy={showCopy ? onCopy : undefined}
          onAddToEditor={showAddToEditor ? onAddToEditor : undefined}
          onAddToDocument={showAddToDocument ? onAddToDocument : undefined}
          onAddToSpreadsheet={showAddToSpreadsheet ? onAddToSpreadsheet : undefined}
          onShare={onShare}
          onSpeak={showCopy ? onSpeak : undefined}
          onRegenerate={isInMessageGroup ? null : onRegenerate}
          onFork={onFork}
              onStartNewTask={onStartNewTask}
          onBookmark={onBookmark}
          onFeedback={onFeedback}
          onEdit={onEdit}
          onReport={onReport}
          onExport={onExport}
          onDelete={onDelete}
          showAttribution={true}
          workActions={message.workActions}
          isLastMessage={isLastMessage}
          modelIds={displayModelIds || message.modelIds}
          agentIds={message.agentIds}
          isAutoMode={isAutoMode}
          inferences={message.inferences}
          isImage={Array.isArray(message.images) && message.images.length > 0}
          isFile={Array.isArray(message.files) && message.files.length > 0}
          isBookmarked={message?.flags?.isSaved === true}
          parameters={message.parameters}
          createdAt={message.timestamp}
          showPreferenceIcons={showPreferenceIcons}
          isGroupedResponsePreferred={message?.flags?.isGroupedResponsePreferred}
          onPreferResponse={onPreferResponse}
          onRejectResponse={onRejectResponse}
        />
      </View>
    )}
    {!isUser && showPreferenceButton && onPreferResponse && (
      <View style={styles.preferenceButtonContainer}>
        <Button
          text="I prefer this response"
          variant={isDarkTheme(theme) ? 'secondary' : 'tertiary'}
          size="small"
          onPress={onPreferResponse}
        />
      </View>
    )}
  </View>
));

// Memoized desktop message component
const DesktopMessage = React.memo(({ 
  conversationId,
  message,
  agents,
  models,
  isUser,
  styles,
  isXS,
  colors,
  theme,
  showActions,
  showCopy,
  showAddToEditor,
  showAddToDocument,
  showAddToSpreadsheet,
  showTimestamps,
  isLastMessage,
  onCopy,
  onAddToEditor,
  onAddToDocument,
  onAddToSpreadsheet,
  onShare,
  onSpeak,
  onRegenerate,
  onFork,
  onStartNewTask,
  onBookmark,
  onFeedback,
  onEdit,
  onReport,
  onDelete,
  onExport,
  onImageClick,
  onButtonClick,
  openAgentDetailsModal,
  openModelDetailsModal,
  userName,
  userAvatar,
  showMessageActions,
  onOpenLightbox,
  onOpenFileEditor,
  showPreferenceButton,
  onPreferResponse,
  onRejectResponse,
  showPreferenceIcons,
  isInMessageGroup,
  isLastGroupInChat,
  isAutoMode = false,
  displayModelIds,
}) => (
  <View style={styles.container}>
    <Avatar
      isUser={isUser}
      userAvatar={userAvatar}
      isXS={isXS}
      colors={colors}
      styles={styles}
      agents={agents}
      models={models}
      openModelDetailsModal={openModelDetailsModal}
      openAgentDetailsModal={openAgentDetailsModal}
    />
    <div 
      style={styles.messageWrapper}
      className={`message-bubble-wrapper ${(message.isError || message?.flags?.isDeleted) ? 'error' : ''}`}
      data-is-user={isUser ? 'true' : 'false'}
      data-message-id={message.id}
      data-is-last-message={isLastMessage ? 'true' : 'false'}
      data-is-animated={showMessageActions ? 'false' : 'true'}
    >
      <MessageContentWrapper
        conversationId={conversationId}
        messageId={message.id}
        message={message}
        agents={agents}
        models={models}
        isUser={isUser}
        styles={styles}
        colors={colors}
        userName={userName}
        showTimestamps={showTimestamps}
        onImageClick={onImageClick}
        onButtonClick={onButtonClick}
        openAgentDetailsModal={openAgentDetailsModal}
        openModelDetailsModal={openModelDetailsModal}
        onOpenLightbox={onOpenLightbox}
        onOpenFileEditor={onOpenFileEditor}
      />
      {showActions && (
        <div 
          style={styles.messageActionsContainer} 
          className="message-bubble-actions-container"
        >
          <MessageActions
              conversationId={conversationId}
              message={message}
            isUser={isUser}
            isVisible={true}
            isCompact={true}
            onCopy={showCopy ? onCopy : undefined}
            onAddToEditor={showAddToEditor ? onAddToEditor : undefined}
            onAddToDocument={showAddToDocument ? onAddToDocument : undefined}
            onAddToSpreadsheet={showAddToSpreadsheet ? onAddToSpreadsheet : undefined}
            onShare={onShare}
            onSpeak={showCopy ? onSpeak : undefined}
            onRegenerate={isInMessageGroup ? null : onRegenerate}
            onFork={onFork}
              onStartNewTask={onStartNewTask}
            onBookmark={onBookmark}
            onFeedback={onFeedback}
            onEdit={onEdit}
            onReport={onReport}
            onDelete={onDelete}
            onExport={onExport}
            showAttribution={true}
            workActions={message.workActions}
            isLastMessage={isLastMessage}
            modelIds={displayModelIds || message.modelIds}
            agentIds={message.agentIds}
            isAutoMode={isAutoMode}
            inferences={message.inferences}
            isImage={Array.isArray(message.images) && message.images.length > 0}
            isFile={Array.isArray(message.files) && message.files.length > 0}
            isBookmarked={message?.flags?.isSaved === true}
            parameters={message.parameters}
            createdAt={message.timestamp}
            showPreferenceIcons={showPreferenceIcons}
            isGroupedResponsePreferred={message?.flags?.isGroupedResponsePreferred}
            onPreferResponse={onPreferResponse}
            onRejectResponse={onRejectResponse}
          />
        </div>
      )}
      {showPreferenceButton && onPreferResponse && (
        <View style={styles.preferenceButtonContainer}>
          <Button
            text="I prefer this response"
            variant={isDarkTheme(theme) ? 'secondary' : 'tertiary'}
            size="small"
            onPress={onPreferResponse}
          />
        </View>
      )}
    </div>
  </View>
));

const MessageSlim = ({
  conversationId,
  message,
  agents,
  models,
  isLastMessage,
  onCopy,
  onAddToEditor,
  onAddToDocument,
  onAddToSpreadsheet,
  onShare,
  onSpeak,
  onRegenerate,
  onFork,
  onStartNewTask,
  onBookmark,
  onFeedback,
  onEdit,
  onReport,
  onDelete,
  onExport,
  onButtonClick,
  onImageClick,
  messageWidth,
  showMessageActions,
  onOpenLightbox,
  onOpenFileEditor,
  isInMessageGroup,
  isLastGroupInChat,
  isAutoMode = false,
  displayModelIds,
  onPreferResponse,
  onRejectResponse,
}) => {
  const { colors, theme } = useTheme();
  const { isXS } = useResponsiveness();
  const isMobile = Platform.OS !== 'web';
  const { openModal } = useModalStore(state => state.actions);
  const isTokenExpired = useAuthStore(state => state.isTokenExpired);

  const token = useAuthStore(state => state.token);
  const userName = useAuthStore(state => state.user?.fullName) || 'You';
  const userAvatar = useAuthStore(state => state.user?.avatarPath) || null;
  const showTimestamps = useUIStore(state => state.showTimestamps);
  
  const isUser = message.role === 'user';
  const isAuthenticated = token && !isTokenExpired();
  const isStreaming = message.isStreaming;
  const showActions = isAuthenticated && !message.isError && !message.isTyping && !isStreaming && !message.isThinking && !message?.flags?.isDeleted;
  const showCopy = message.content && message.content.length > 0;
  // Image-only generations have empty content; still offer Add to document.
  const showAddToEditor = showCopy || messageHasEditorInsertContent(message);
  const showAddToDocument = !isUser && showAddToEditor;
  const showAddToSpreadsheet = !isUser && messageHasMarkdownTable(message);
  // Check if a preference has been locally selected for this group
  const hasSelectedPreference = message.flags?.hasSelectedPreferenceLocally;
  // Show button only on last group AND if no preference has been selected locally yet
  const showPreferenceButton = isInMessageGroup && isLastGroupInChat && !isUser && message.content && !hasSelectedPreference;
  // Show icons if not last group, OR if last group but preference has been selected
  const showPreferenceIcons = isInMessageGroup && !isUser && message.content && (!isLastGroupInChat || hasSelectedPreference);
  
  const styles = useMemo(() => 
    createStyles(colors, isXS, isUser, isLastMessage, messageWidth, showMessageActions),
    [colors, isXS, isUser, isLastMessage, messageWidth, showMessageActions]
  );

  const openAgentDetailsModal = useCallback((agent) => {
    openModal('AgentDetailsModal', { agentId: agent.id });
  }, [openModal]);

  const openModelDetailsModal = useCallback((model) => {
    openModal('ModelDetailsModal', { modelId: model.id });
  }, [openModal]);

  if (isMobile) {
    return (
      <MobileMessage
        conversationId={conversationId}
        message={message}
        agents={agents}
        models={models}
        userName={userName}
        userAvatar={userAvatar}
        isUser={isUser}
        isLastMessage={isLastMessage}
        styles={styles}
        colors={colors}
        theme={theme}
        isXS={isXS}
        showActions={showActions}
        showCopy={showCopy}
        showAddToEditor={showAddToEditor}
        showAddToDocument={showAddToDocument}
        showAddToSpreadsheet={showAddToSpreadsheet}
        showTimestamps={showTimestamps}
        onCopy={onCopy}
        onAddToEditor={onAddToEditor}
        onAddToDocument={onAddToDocument}
        onAddToSpreadsheet={onAddToSpreadsheet}
        onShare={onShare}
        onSpeak={onSpeak}
        onRegenerate={onRegenerate}
        onFork={onFork}
              onStartNewTask={onStartNewTask}
        onBookmark={onBookmark}
        onFeedback={onFeedback}
        onEdit={onEdit}
        onReport={onReport}
        onDelete={onDelete}
        onExport={onExport}
        onImageClick={onImageClick}
        onButtonClick={onButtonClick}
        openAgentDetailsModal={openAgentDetailsModal}
        openModelDetailsModal={openModelDetailsModal}
        onOpenLightbox={onOpenLightbox}
        onOpenFileEditor={onOpenFileEditor}
        showPreferenceButton={showPreferenceButton}
        showPreferenceIcons={showPreferenceIcons}
        onPreferResponse={onPreferResponse}
        onRejectResponse={onRejectResponse}
        isInMessageGroup={isInMessageGroup}
        isLastGroupInChat={isLastGroupInChat}
        isAutoMode={isAutoMode}
        displayModelIds={displayModelIds}
      />
    );
  }

  return (
    <DesktopMessage
      conversationId={conversationId}
      message={message}
      agents={agents}
      models={models}
      userName={userName}
      userAvatar={userAvatar}
      isUser={isUser}
      styles={styles}
      colors={colors}
      theme={theme}
      isXS={isXS}
      showActions={showActions}
      showMessageActions={showMessageActions}
      showCopy={showCopy}
      showAddToEditor={showAddToEditor}
      showAddToDocument={showAddToDocument}
      showAddToSpreadsheet={showAddToSpreadsheet}
      showTimestamps={showTimestamps}
      isLastMessage={isLastMessage}
      onCopy={onCopy}
      onAddToEditor={onAddToEditor}
      onAddToDocument={onAddToDocument}
      onAddToSpreadsheet={onAddToSpreadsheet}
      onShare={onShare}
      onSpeak={onSpeak}
      onRegenerate={onRegenerate}
      onFork={onFork}
              onStartNewTask={onStartNewTask}
      onBookmark={onBookmark}
      onFeedback={onFeedback}
      onEdit={onEdit}
      onExport={onExport}
      onReport={onReport}
      onDelete={onDelete}
      onImageClick={onImageClick}
      onButtonClick={onButtonClick}
      openAgentDetailsModal={openAgentDetailsModal}
      openModelDetailsModal={openModelDetailsModal}
      onOpenLightbox={onOpenLightbox}
      onOpenFileEditor={onOpenFileEditor}
      showPreferenceButton={showPreferenceButton}
      showPreferenceIcons={showPreferenceIcons}
      onPreferResponse={onPreferResponse}
      onRejectResponse={onRejectResponse}
      isInMessageGroup={isInMessageGroup}
      isLastGroupInChat={isLastGroupInChat}
      isAutoMode={isAutoMode}
      displayModelIds={displayModelIds}
    />
  );
};

export default React.memo(MessageSlim);
